/**
 * Analytics Logger
 * Provides structured logging for analytics events
 */

// Event types for analytics logging
export enum AnalyticsEventType {
  CONVERSATION_CREATED = 'conversation_created',
  CONVERSATION_UPDATED = 'conversation_updated',
  CONVERSATION_COMPLETED = 'conversation_completed',
  USER_RESPONSE_SAVED = 'user_response_saved',
  VALIDATION_PERFORMED = 'validation_performed',
  ERROR_OCCURRED = 'error_occurred'
}

// Interface for analytics log entries
export interface AnalyticsLogEntry {
  eventType: AnalyticsEventType;
  chatbotId?: string;
  conversationId?: string;
  sessionId?: string;
  timestamp?: string;
  metadata?: Record<string, any>;
  error?: {
    message: string;
    code?: string;
    details?: any;
  };
}

// Create a custom event for analytics logs
export const logAnalyticsEvent = (entry: AnalyticsLogEntry): void => {
  // Add timestamp if not provided
  if (!entry.timestamp) {
    entry.timestamp = new Date().toISOString();
  }

  // Create and dispatch the event for test capture
  const event = new CustomEvent('chat-analytics-event', {
    detail: entry
  });

  // Dispatch to document for Playwright to capture
  document.dispatchEvent(event);

  // Format the log message
  const logPrefix = `[ANALYTICS] [${entry.eventType}]`;
  
  // Log to console with appropriate level
  if (entry.eventType === AnalyticsEventType.ERROR_OCCURRED) {
    console.error(
      logPrefix,
      entry.error?.message || 'Unknown error',
      {
        ...entry,
        // Format for better readability
        timestamp: entry.timestamp,
        chatbotId: entry.chatbotId,
        conversationId: entry.conversationId,
        sessionId: entry.sessionId
      }
    );
  } else {
    console.debug(
      logPrefix,
      {
        ...entry,
        // Format for better readability
        timestamp: entry.timestamp,
        chatbotId: entry.chatbotId,
        conversationId: entry.conversationId,
        sessionId: entry.sessionId
      }
    );
  }
};

/**
 * Log conversation created event
 */
export const logConversationCreated = (
  chatbotId: string,
  conversationId: string,
  sessionId: string,
  metadata?: Record<string, any>
): void => {
  logAnalyticsEvent({
    eventType: AnalyticsEventType.CONVERSATION_CREATED,
    chatbotId,
    conversationId,
    sessionId,
    metadata
  });
};

/**
 * Log conversation completed event
 */
export const logConversationCompleted = (
  chatbotId: string,
  conversationId: string,
  sessionId: string,
  metadata?: Record<string, any>
): void => {
  logAnalyticsEvent({
    eventType: AnalyticsEventType.CONVERSATION_COMPLETED,
    chatbotId,
    conversationId,
    sessionId,
    metadata
  });
};

/**
 * Log error event
 */
export const logAnalyticsError = (
  message: string,
  chatbotId?: string,
  conversationId?: string,
  sessionId?: string,
  details?: any
): void => {
  logAnalyticsEvent({
    eventType: AnalyticsEventType.ERROR_OCCURRED,
    chatbotId,
    conversationId,
    sessionId,
    error: {
      message,
      details
    }
  });
};
